/**
 * BotEnvModal - view / edit one bot's /opt/audiosocket-bot/.env
 * ============================================================
 * Used by the bot rows on Servers, Containers and Monitoring.
 * Backend: GET / PUT /api/audiosocket-bots/machines/:id/env
 */
import React, { useState, useEffect } from 'react'
import { X, Loader2, Trash2, RotateCw, Plus, RefreshCw, Save } from 'lucide-react'
import { audiosocketBotsApi } from '../services/api'

// Settings editor for one bot's /opt/audiosocket-bot/.env. Sends only what
// changed (set / unset); secret values arrive masked and are only sent when
// retyped. "Save & restart" restarts the bot unless it is on a call.
export default function BotEnvModal({ machine, onClose }) {
    const [rows, setRows] = useState(null)       // [{ key, value, orig, secret, isNew, removed }]
    const [error, setError] = useState('')
    const [saving, setSaving] = useState(false)
    const [result, setResult] = useState('')
    const [force, setForce] = useState(false)

    async function load() {
        setError(''); setRows(null)
        try {
            const r = await audiosocketBotsApi.getMachineEnv(machine.id)
            setRows(r.data.env.map(e => ({ ...e, orig: e.value })))
        } catch (e) { setError(e.response?.data?.error || e.message); setRows([]) }
    }
    useEffect(() => { load() /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [machine.id])

    const update = (i, patch) => setRows(rs => rs.map((r, j) => j === i ? { ...r, ...patch } : r))
    const changes = (rows || []).reduce((acc, r) => {
        const key = r.key.trim()
        if (r.removed) { if (!r.isNew) acc.unset.push(key) }
        else if (key && (r.isNew || r.value !== r.orig)) acc.set[key] = r.value
        return acc
    }, { set: {}, unset: [] })
    const changeCount = Object.keys(changes.set).length + changes.unset.length

    async function save(restart) {
        setSaving(true); setError(''); setResult('')
        try {
            const r = await audiosocketBotsApi.saveMachineEnv(machine.id, { ...changes, restart, force })
            setRows(r.data.env.map(e => ({ ...e, orig: e.value })))
            const rs = r.data.restart
            setResult(`Saved (backup ${r.data.backup.split('/').pop()}). ` +
                (!rs.requested ? 'Restart the bot for it to take effect.'
                    : rs.done ? 'Bot restarted.' : `Not restarted: ${rs.reason}`))
        } catch (e) { setError(e.response?.data?.error || e.message) }
        finally { setSaving(false) }
    }

    const cell = { padding: '4px 6px', fontFamily: 'monospace', fontSize: 12 }
    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal" style={{ maxWidth: 760 }} onClick={e => e.stopPropagation()}>
                <div className="modal-header">
                    <h3>Bot {machine.sip_user} settings — /opt/audiosocket-bot/.env</h3>
                    <button className="modal-close" onClick={onClose}><X size={18} /></button>
                </div>
                <div className="modal-body">
                    {rows === null ? (
                        <div style={{ opacity: 0.6, fontSize: 13 }}><Loader2 size={14} className="spinning" /> reading .env…</div>
                    ) : (
                        <table className="vm-table" style={{ marginBottom: 10 }}>
                            <thead><tr><th style={{ width: '38%' }}>Key</th><th>Value</th><th style={{ width: 36 }}></th></tr></thead>
                            <tbody>
                                {rows.map((r, i) => {
                                    const changed = r.isNew || r.value !== r.orig
                                    return (
                                        <tr key={i} style={{ opacity: r.removed ? 0.4 : 1, background: changed && !r.removed ? 'rgba(59,130,246,0.08)' : undefined }}>
                                            <td style={cell}>
                                                {r.isNew
                                                    ? <input className="form-input" style={cell} value={r.key} placeholder="NEW_KEY"
                                                        onChange={e => update(i, { key: e.target.value.toUpperCase() })} />
                                                    : <span style={{ textDecoration: r.removed ? 'line-through' : 'none' }}>{r.key}</span>}
                                            </td>
                                            <td style={cell}>
                                                <input className="form-input" style={{ ...cell, width: '100%' }} value={r.value} disabled={r.removed}
                                                    placeholder={r.secret ? 'type a new value to replace' : ''}
                                                    onFocus={() => { if (r.secret && r.value === r.orig) update(i, { value: '' }) }}
                                                    onChange={e => update(i, { value: e.target.value })} />
                                            </td>
                                            <td style={cell}>
                                                <button className={`action-btn ${r.removed ? '' : 'danger'}`} title={r.removed ? 'Keep' : 'Remove'}
                                                    onClick={() => r.isNew ? setRows(rs => rs.filter((_, j) => j !== i)) : update(i, { removed: !r.removed })}>
                                                    {r.removed ? <RotateCw size={12} /> : <Trash2 size={12} />}
                                                </button>
                                            </td>
                                        </tr>
                                    )
                                })}
                            </tbody>
                        </table>
                    )}
                    <button className="btn btn-secondary btn-sm" disabled={rows === null}
                        onClick={() => setRows(rs => [...rs, { key: '', value: '', orig: '', isNew: true }])}>
                        <Plus size={14} /> Add setting
                    </button>
                    <div style={{ fontSize: 11, opacity: 0.65, marginTop: 10 }}>
                        Changes apply after the bot restarts. The previous file is kept as a .env.bak-… backup on the bot.
                        Changing DASHBOARD_BASE_URL, SIP_USER or TEMPLATE_ID can disconnect the bot — double-check before saving.
                    </div>
                    {error && <div className="test-result error" style={{ marginTop: 10, fontSize: 12 }}>{error}</div>}
                    {result && <div className="test-result success" style={{ marginTop: 10, fontSize: 12 }}>{result}</div>}
                </div>
                <div className="modal-footer" style={{ display: 'flex', alignItems: 'center', gap: 8, justifyContent: 'flex-end' }}>
                    <label style={{ fontSize: 12, marginRight: 'auto', display: 'flex', gap: 6, alignItems: 'center' }}>
                        <input type="checkbox" checked={force} onChange={e => setForce(e.target.checked)} />
                        Restart even if on a call
                    </label>
                    <button className="btn btn-secondary btn-sm" onClick={load} disabled={saving}><RefreshCw size={14} /> Reload</button>
                    <button className="btn btn-secondary btn-sm" onClick={() => save(false)} disabled={saving || !changeCount}>
                        <Save size={14} /> Save
                    </button>
                    <button className="btn btn-primary btn-sm" onClick={() => save(true)} disabled={saving || !changeCount}>
                        {saving ? <Loader2 size={14} className="spinning" /> : <RotateCw size={14} />} Save & restart{changeCount ? ` (${changeCount})` : ''}
                    </button>
                </div>
            </div>
        </div>
    )
}
